import { Awaitable } from './awaitable'

/**
 * A decorator for class fields that handles asynchronous values with tracking.
 *
 * The `trackedAsync` decorator creates a property that wraps an {@link Awaitable} instance,
 * which tracks the loading state, value, and errors of asynchronous operations.
 *
 * When a Promise is assigned to a property decorated with `trackedAsync`, it will:
 * - Track the loading state (`loading` property)
 * - Store the resolved value (`value` property)
 * - Capture any errors that occur (`error` property)
 * - Provide helper properties like `resolved` and `rejected`
 *
 * This makes it easy to handle async operations in Ember/Glimmer components
 * with automatic tracking and template updates.
 *
 * @example
 * ```ts
 * class MyComponent {
 *   @trackedAsync() data;
 *
 *   constructor() {
 *     this.data.promise = fetchSomeData(); // Promise
 *   }
 * }
 *
 * // In template:
 * // {{#if this.data.loading}}Loading...{{/if}}
 * // {{#if this.data.error}}Error: {{this.data.error}}{{/if}}
 * // {{#if this.data.resolved}}Data: {{this.data.value}}{{/if}}
 * ```
 */
export function trackedAsync(): any {
  const backingMap = new WeakMap<object, Awaitable<any>>()

  return function (_target: any, _context: ClassFieldDecoratorContext) {
    return {
      get() {
        let instance = backingMap.get(this)

        if (!instance) {
          instance = new Awaitable()
          backingMap.set(this, instance)
        }

        return instance
      },
      set(value: any) {
        let instance = backingMap.get(this)

        if (!instance) {
          instance = new Awaitable()
          backingMap.set(this, instance)
        }

        if (value instanceof Awaitable) {
          instance.promise = value.promise
        } else {
          instance = value
        }

      },
      enumerable: true,
      configurable: true
    }
  }
}
